import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Chip,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { useAuth } from "../contexts/AuthContext";
import { useLocale } from "../contexts/LocaleContext";
import {
  getTeamMembers,
  updateMemberRole,
  removeTeamMember,
  updateTeamName,
} from "../utils/firebase";

function formatDate(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function TeamPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { localizePath } = useLocale();
  const navigate = useNavigate();
  const [teamName, setTeamName] = useState(null);
  const [createdBy, setCreatedBy] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [error, setError] = useState(null);

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);

  const [confirmRemove, setConfirmRemove] = useState(null);

  const loadMembers = useCallback(async () => {
    try {
      const result = await getTeamMembers();
      setTeamName(result.data.teamName);
      setCreatedBy(result.data.createdBy || null);
      setMembers(result.data.members || []);
    } catch (err) {
      console.error("Failed to load team:", err);
      setError(err.message || t("teamPage.failedLoad"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleRoleChange = async (targetUserId, newRole) => {
    setUpdatingUserId(targetUserId);
    setError(null);
    try {
      await updateMemberRole({ targetUserId, newRole });
      await loadMembers();
    } catch (err) {
      setError(err.message || t("teamPage.failedUpdateRole"));
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleConfirmRemove = async () => {
    if (!confirmRemove) return;
    const targetUserId = confirmRemove.userId;
    setRemovingUserId(targetUserId);
    setError(null);
    try {
      await removeTeamMember({ targetUserId });
      setConfirmRemove(null);
      await loadMembers();
    } catch (err) {
      setError(err.message || t("teamPage.failedRemove"));
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleSaveRename = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === teamName) {
      setRenameOpen(false);
      return;
    }
    setRenameSaving(true);
    setError(null);
    try {
      await updateTeamName({ teamName: trimmed });
      setTeamName(trimmed);
      setRenameOpen(false);
    } catch (err) {
      setError(err.message || t("teamPage.failedRename"));
    } finally {
      setRenameSaving(false);
    }
  };

  const currentMember = members.find((m) => m.userId === user?.uid);
  const isAdmin = currentMember?.role === "admin";

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (members.length === 0) {
    return (
      <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh" }}>
        <Container maxWidth="lg" sx={{ py: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#111827", mb: 2 }}>
            {t("teamPage.title")}
          </Typography>
          <Typography sx={{ color: "#6b7280" }}>
            {t("teamPage.emptyState")}
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 5, px: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: '"Cal Sans", "Inter", sans-serif',
              fontWeight: 600,
              color: "#111827",
            }}
          >
            {teamName || t("teamPage.title")}
          </Typography>
          {isAdmin && (
            <IconButton
              size="small"
              aria-label={t("teamPage.renameTeam")}
              onClick={() => {
                setRenameValue(teamName || "");
                setRenameOpen(true);
              }}
              sx={{ color: "#6b7280" }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        {error && (
          <Typography color="error" sx={{ mb: 2, fontSize: 14 }}>
            {error}
          </Typography>
        )}
        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                {isAdmin && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => {
                const isSelf = m.userId === user?.uid;
                const isFounder = m.userId === createdBy;
                const canDrillIn = isAdmin && !isSelf;
                const canRemove = isAdmin && !isSelf && !isFounder;
                const goToMember = () =>
                  navigate(localizePath(`/team/member/${m.userId}`));
                return (
                  <TableRow
                    key={m.userId}
                    hover={canDrillIn}
                    onClick={canDrillIn ? goToMember : undefined}
                    sx={{
                      cursor: canDrillIn ? "pointer" : "default",
                    }}
                  >
                    <TableCell>{m.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={m.role}
                        size="small"
                        sx={{
                          bgcolor: m.role === "admin" ? "#111827" : "#f3f4f6",
                          color: m.role === "admin" ? "#fff" : "#374151",
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatDate(m.joinedAt)}</TableCell>
                    {isAdmin && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {canDrillIn && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityIcon />}
                              onClick={goToMember}
                              sx={{ textTransform: "none" }}
                            >
                              {t("teamPage.viewCalls")}
                            </Button>
                          )}
                          {!isSelf && m.role === "member" && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={updatingUserId === m.userId}
                              onClick={() => handleRoleChange(m.userId, "admin")}
                              sx={{ textTransform: "none" }}
                            >
                              {updatingUserId === m.userId ? "…" : "Make admin"}
                            </Button>
                          )}
                          {!isSelf && m.role === "admin" && !isFounder && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={updatingUserId === m.userId}
                              onClick={() => handleRoleChange(m.userId, "member")}
                              sx={{ textTransform: "none" }}
                            >
                              {updatingUserId === m.userId ? "…" : t("teamPage.makeMember")}
                            </Button>
                          )}
                          {canRemove && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<PersonRemoveIcon />}
                              disabled={removingUserId === m.userId}
                              onClick={() => setConfirmRemove(m)}
                              sx={{ textTransform: "none" }}
                            >
                              {removingUserId === m.userId
                                ? t("teamPage.removing")
                                : t("teamPage.remove")}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      <Dialog open={renameOpen} onClose={() => !renameSaving && setRenameOpen(false)}>
        <DialogTitle>{t("teamPage.renameTeam")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSaveRename();
              }
            }}
            disabled={renameSaving}
            inputProps={{ maxLength: 80 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRenameOpen(false)}
            disabled={renameSaving}
            sx={{ textTransform: "none" }}
          >
            {t("teamPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRename}
            disabled={renameSaving || !renameValue.trim()}
            sx={{
              textTransform: "none",
              bgcolor: "#111827",
              "&:hover": { bgcolor: "#374151" },
            }}
          >
            {renameSaving ? t("teamPage.saving") : t("teamPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmRemove} onClose={() => !removingUserId && setConfirmRemove(null)}>
        <DialogTitle>{t("teamPage.confirmRemoveTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("teamPage.confirmRemoveBody", { email: confirmRemove?.email })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmRemove(null)}
            disabled={!!removingUserId}
            sx={{ textTransform: "none" }}
          >
            {t("teamPage.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRemove}
            disabled={!!removingUserId}
            sx={{ textTransform: "none" }}
          >
            {removingUserId ? t("teamPage.removing") : t("teamPage.confirmRemove")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
